import React, { useEffect, useState } from 'react';
import { Drawer } from 'antd';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { DateRangePickerInput } from 'v3/Components/Form/DateTimePickers/DateRangePicker';
import { PrimaryButton, TertiaryButton, LinkButton } from 'v3/Components/Shared/Buttons';
import { useToasts } from 'Context';
import { buildEndpoint, useApiWorker, useDataPoster, useHasPermissionsV3 } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { AxiosError } from 'axios';

const MAX_WINDOWS = 5;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const formatUnavailabilityDate = (start: string, end: string): string => {
  const s = new Date(start);
  const e = new Date(end);
  const sYear  = s.getUTCFullYear();
  const eYear  = e.getUTCFullYear();
  const sDay   = s.getUTCDate();
  const eDay   = e.getUTCDate();
  const sMonth = MONTH_NAMES[s.getUTCMonth()];
  const eMonth = MONTH_NAMES[e.getUTCMonth()];

  if (start.slice(0, 10) === end.slice(0, 10)) return `${sMonth} ${sDay}, ${eYear}`;
  if (sYear === eYear) return `${sMonth} ${sDay} - ${eMonth} ${eDay}, ${eYear}`;
  return `${sMonth} ${sDay}, ${sYear} - ${eMonth} ${eDay}, ${eYear}`;
};

const parseRangeValue = (value: string): { start: string; end: string } | null => {
  const idx = value.indexOf('|');
  if (idx === -1) return null;
  const toISO = (mmddyyyy: string) => {
    const [mm, dd, yyyy] = mmddyyyy.split('/');
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T00:00:00Z`;
  };
  return { start: toISO(value.slice(0, idx)), end: toISO(value.slice(idx + 1)) };
};

const toMMDDYYYY = (iso: string) => {
  const d = new Date(iso);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getUTCFullYear()}`;
};

export type UnavailabilityEntry = {
  id: string;
  startDate: string;
  endDate: string;
};

// ── Drawer ────────────────────────────────────────────────────────────────────

type DrawerProps = {
  visible: boolean;
  reviewerId?: string;
  entry?: UnavailabilityEntry;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
};

const UnavailabilityDrawer: React.FC<DrawerProps> = ({
  visible, reviewerId, entry, onClose, onSaved, onDeleted,
}) => {
  const toast  = useToasts();
  const api    = useApiWorker('3.0');
  const isEdit = !!entry;

  const [rangeValue, setRangeValue] = useState<string>('');
  const [rangeError, setRangeError] = useState('');

  useEffect(() => {
    if (visible) {
      setRangeValue(entry ? `${toMMDDYYYY(entry.startDate)}|${toMMDDYYYY(entry.endDate)}` : '');
      setRangeError('');
    }
  }, [visible]);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [postAdd] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'unavailability'),
    undefined, false, '3.0'
  );
  const [postUpdate] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'unavailability', entry?.id),
    undefined, true, '3.0'
  );

  const validate = (): { startDate: string; endDate: string } | null => {
    if (!rangeValue) { setRangeError('Date(s) Unavailable is required'); return null; }
    const parsed = parseRangeValue(rangeValue);
    if (!parsed) { setRangeError('Please select both start and end dates'); return null; }
    const today = new Date(); today.setUTCHours(0, 0, 0, 0);
    if (new Date(parsed.start) > new Date(parsed.end)) { setRangeError('Start Date cannot be after End Date'); return null; }
    if (new Date(parsed.end) < today) { setRangeError('End Date must be today or in the future'); return null; }
    setRangeError('');
    return { startDate: parsed.start, endDate: parsed.end };
  };

  const handleSave = async () => {
    const payload = validate();
    if (!payload) return;
    setSaving(true);
    try {
      const resp: any = isEdit ? await postUpdate(payload) : await postAdd(payload);
      if (resp?.status === 200) {
        toast.add(isEdit ? 'Unavailability updated' : 'Unavailability added', { type: 'success', position: 'bottom-left' });
        onSaved();
        onClose();
      } else {
        const msg = (resp as AxiosError<any>)?.response?.data?.message;
        toast.add(msg || 'Failed to save. Please try again.', { type: 'error', position: 'bottom-left' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!entry) return;
    setDeleting(true);
    try {
      await api.delete(buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'unavailability', entry.id));
      toast.add('Unavailability removed', { type: 'success', position: 'bottom-left' });
      onDeleted();
      onClose();
    } catch {
      toast.add('Failed to delete. Please try again.', { type: 'error', position: 'bottom-left' });
    } finally {
      setDeleting(false);
    }
  };

  const drawerTitle = (
    <div className="unavailability-drawer__header-inner">
      <span className="unavailability-drawer__title">
        {isEdit ? 'Update Unavailability' : 'Add Unavailability'}
      </span>
      <button className="unavailability-drawer__close-btn" onClick={onClose}>
        <CloseIcon />
      </button>
    </div>
  );

  const footer = (
    <div className="sl-drawer-footer">
      <TertiaryButton title="Cancel" onClick={onClose} disabled={saving || deleting} />
      <PrimaryButton
        title={saving ? undefined : isEdit ? 'Save' : 'Add'}
        onClick={handleSave}
        disabled={saving || deleting}
        style={{ width: 'auto', padding: '0 20px' }}
      >
        {saving && <CircularProgress size={14} thickness={5} style={{ color: 'inherit' }} />}
      </PrimaryButton>
    </div>
  );

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      title={drawerTitle}
      width={720}
      closable={false}
      footer={footer}
      destroyOnClose
      className="unavailability-drawer"
    >
      <div className={`unavailability-drawer__body${isEdit ? ' unavailability-drawer__body--edit' : ''}`}>
        {!isEdit && (
          <p className="unavailability-drawer__description">
            Please provide the dates that you will be out of office and unable to take cases.
          </p>
        )}
        <div className="unavailability-drawer__picker">
          <DateRangePickerInput
            label="Date(s) Unavailable"
            required
            valueString={rangeValue}
            onChange={(e) => { setRangeValue(e.target.value); setRangeError(''); }}
            error={rangeError}
            dateOnly
            clearable
          />
        </div>

        {isEdit && (
          <div className="unavailability-drawer__delete-section">
            <div className="unavailability-drawer__divider" />
            <button
              className="unavailability-drawer__delete-btn"
              onClick={handleDelete}
              disabled={deleting || saving}
            >
              {deleting
                ? <CircularProgress size={16} style={{ color: 'inherit' }} />
                : <CloseIcon />
              }
              <span>Delete Unavailability</span>
            </button>
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ── Card content ──────────────────────────────────────────────────────────────

type Props = {
  entries: UnavailabilityEntry[];
  reviewerId?: string;
  onRefresh: () => void;
};

const UnavailabilityContent: React.FC<Props> = ({ entries, reviewerId, onRefresh }) => {
  const hasPermissionsV3 = useHasPermissionsV3();
  const canManage = hasPermissionsV3(PermissionCodesV3.Reviewer.Unavailability.Manage);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeEntry, setActiveEntry]     = useState<UnavailabilityEntry | undefined>(undefined);

  const openAdd  = () => { setActiveEntry(undefined); setDrawerVisible(true); };
  const openEdit = (entry: UnavailabilityEntry) => { setActiveEntry(entry); setDrawerVisible(true); };
  const canAdd   = canManage && entries.length < MAX_WINDOWS;

  return (
    <div className="overview-unavailability">
      {entries.map((entry) => (
        <div key={entry.id} className="overview-unavailability__entry">
          <div className="overview-unavailability__accent" />
          <div className="overview-unavailability__content">
            <p className="overview-unavailability__date">
              {formatUnavailabilityDate(entry.startDate, entry.endDate)}
            </p>
            {canManage && (
              <LinkButton onClick={() => openEdit(entry)} className="overview-unavailability__update">
                Update
              </LinkButton>
            )}
          </div>
        </div>
      ))}

      {canManage && (
        <button
          className={`overview-unavailability__add-btn${!canAdd ? ' disabled' : ''}`}
          onClick={canAdd ? openAdd : undefined}
          disabled={!canAdd}
          title={!canAdd ? `Maximum of ${MAX_WINDOWS} entries reached` : undefined}
        >
          <AddIcon fontSize="small" />
          Add Unavailability
        </button>
      )}

      <UnavailabilityDrawer
        key={activeEntry?.id ?? 'add'}
        visible={drawerVisible}
        reviewerId={reviewerId}
        entry={activeEntry}
        onClose={() => setDrawerVisible(false)}
        onSaved={onRefresh}
        onDeleted={onRefresh}
      />
    </div>
  );
};

export default UnavailabilityContent;
